import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';

import { ParadexWebSocket } from '#/api/ws/ws';

import createSafeContext from '#/utils/createSafeContext';
import { getStorageItem, setStorageItem } from '#/utils/localStorage';
import { AsyncResult } from '#/utils/types';

import { useParadexWebSocket } from '#/features/ws/ws-context';
import { AuthRequest } from '#/features/wallet/chain-l2';
import actionPerformAuth from '#/features/auth/auth-context/action-perform-auth';
import { StarknetSignature } from '#/features/starknet-wallet/wallet';
import {
  STORAGE_KEY_ETHEREUM_WALLET_ADDRESS,
  getStarknetWalletSeedStorageKey,
} from '#/features/storage/storage';
import {
  EthereumChainConfigurable,
  StarknetChainConfigurable,
} from '#/features/wallet/wallet-context';
import { useSystemConfig } from '#/features/system/system-config-context';

interface SignedRequest {
  readonly request: AuthRequest;
  readonly signature: StarknetSignature;
}

export interface AuthState {
  readonly token: string | null;
  readonly signedRequest: SignedRequest | null;
  readonly error: string | null;
  readonly walletSeed: string | null;
  readonly ethereumAddress: string | null;
  readonly rememberMe: boolean;
}

const INITIAL_STATE: AuthState = {
  token: null,
  signedRequest: null,
  error: null,
  walletSeed: null,
  ethereumAddress: null,
  rememberMe: true,
};

export interface AuthActions {
  readonly performAuth: () => AsyncResult<void>;
  readonly setRememberMe: (rememberMe: boolean) => void;
  readonly rememberUser: (ethereumAddress: string, walletSeed: string) => void;
  readonly forgetUser: () => void;
  /**
   * Removes the token from the state to indicate that no requests to
   * private endpoints that require authentication should be made.
   */
  readonly resetToken: () => void;
  readonly reset: () => void;
}

interface AuthView {
  readonly rememberMe: boolean;
  readonly isAuthTokenAvailable: boolean;
}

export const [useAuthState, AuthStateCtx] =
  createSafeContext<[AuthState, Dispatch<SetStateAction<AuthState>>]>(
    'AuthState',
  );
export const [useAuthView, AuthViewCtx] =
  createSafeContext<AuthView>('AuthView');
export const [useAuthActions, AuthActionsCtx] =
  createSafeContext<AuthActions>('AuthActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function AuthStateProvider(props: ProviderProps) {
  const config = useSystemConfig();
  const ethereumChainId = config.ethereum.chainId;

  const state = useState(() => ({
    ...INITIAL_STATE,
    walletSeed: getStorageItem<string>(
      getStarknetWalletSeedStorageKey(ethereumChainId),
    ),
    ethereumAddress: getStorageItem<string>(
      STORAGE_KEY_ETHEREUM_WALLET_ADDRESS,
    ),
  }));

  return <AuthStateCtx value={state}>{props.children}</AuthStateCtx>;
}

export function AuthViewProvider(props: ProviderProps) {
  const [authState] = useAuthState();

  const view = useMemo(() => createView(authState), [authState]);

  return <AuthViewCtx value={view}>{props.children}</AuthViewCtx>;
}

export function AuthActionsProvider(props: ProviderProps) {
  const [authState, setAuthState] = useAuthState();
  const ws = useParadexWebSocket();
  const config = useSystemConfig();

  const options = useMemo(
    () => ({
      starknetChainId: config.starknet.chainId,
      ethereumChainId: config.ethereum.chainId,
    }),
    [config.ethereum.chainId, config.starknet.chainId],
  );

  const actions = useMemo(
    () => createActions(authState, setAuthState, ws, options),
    [authState, setAuthState, ws, options],
  );

  return <AuthActionsCtx value={actions}>{props.children}</AuthActionsCtx>;
}

function createView(authState: AuthState): AuthView {
  const isAuthTokenAvailable = authState.token != null;

  return {
    rememberMe: authState.rememberMe,
    isAuthTokenAvailable,
  };
}

function createActions(
  authState: AuthState,
  setAuthState: Dispatch<SetStateAction<AuthState>>,
  ws: ParadexWebSocket | undefined,
  options: StarknetChainConfigurable & EthereumChainConfigurable,
): AuthActions {
  const performAuth = actionPerformAuth(authState, setAuthState, ws, {
    starknetChainId: options.starknetChainId,
  });

  const setRememberMe = (rememberMe: boolean) => {
    setAuthState((state) => ({ ...state, rememberMe }));
  };

  const rememberUser = (ethereumAddress: string, walletSeed: string) => {
    setStorageItem(STORAGE_KEY_ETHEREUM_WALLET_ADDRESS, ethereumAddress);
    setStorageItem(
      getStarknetWalletSeedStorageKey(options.ethereumChainId),
      walletSeed,
    );
    setAuthState((state) => ({ ...state, ethereumAddress, walletSeed }));
  };

  const forgetUser = () => {
    setStorageItem(STORAGE_KEY_ETHEREUM_WALLET_ADDRESS, null);
    setStorageItem(
      getStarknetWalletSeedStorageKey(options.ethereumChainId),
      null,
    );
    setAuthState((state) => ({
      ...state,
      ethereumAddress: null,
      walletSeed: null,
    }));
  };

  const resetToken = () => {
    setAuthState((state) => ({
      ...state,
      token: null,
    }));
  };

  const reset = () => {
    forgetUser();
    setAuthState(INITIAL_STATE);
  };

  return {
    performAuth,
    setRememberMe,
    rememberUser,
    forgetUser,
    resetToken,
    reset,
  };
}
